import moment from 'moment';
import { DayOfWeek, LocalDate, LocalDateTime, LocalTime, TemporalAdjusters } from '@js-joda/core';
import {
   dateCellFormatter,
   dateFormatter,
   dateFullFormatter,
   dateLongFormatter,
   dateMediumFormatter,
   dateWithDowFormatter,
   dateYearLongFormatter,
   hourFormatter,
   monthDayFormatter,
   monthLongFormatter,
   monthShortFormatter,
   monthYearFormatter,
   timeFormatter,
   timeFormatterWithSeconds,
   weekdayLongFormatter,
   weekdayNarrowFormatter,
   weekdayShortFormatter,
   yearLongFormatter
} from './DateTimeFormatters';
import { formatMessageBundle } from '../FormatMessage';
import messages from '../../localized-messages';
import { currentDate } from '../../dates/CurrentDateProvider';
import {
   jodaDateTypeAtUtcToNativeDate,
   localTimeAtUtcToNativeDate
} from '../../dates/DateAndTimeUtils';
import { getEnvironment, UniFocusEnvironment } from '../../environment/Environment';
import { removeNonPrintableCharacters } from '../../utilities/stringUtilities';

/**
 * Formatting of dates based on current locale.
 */

export type DateType = Date | LocalDate | LocalDateTime;

const normalizedDate = (date: DateType | LocalTime): Date => {
   if (date instanceof LocalTime) {
      return localTimeAtUtcToNativeDate(date);
   }
   return date instanceof Date ? date : jodaDateTypeAtUtcToNativeDate(date);
};

/**
 * Call this function to convert Date object to localized string with month, day and year information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month, day and year information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDate: string = formatDate(date);
 * ```
 */
export const formatDate = (date: DateType,
                           environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(dateFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with month, day and year long information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month, day and year long information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateYearLong: string = formatDateYearLong(date);
 * ```
 */
export const formatDateYearLong = (date: DateType,
                                   environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(dateYearLongFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with short month, day and year information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with short month, day and year information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateMedium: string = formatDateMedium(date);
 * ```
 */
export const formatDateMedium = (date: DateType,
                                 environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(dateMediumFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with month, day and year information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month, day and year information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateLong: string = formatDateLong(date);
 * ```
 */
export const formatDateLong = (date: DateType,
                               environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(dateLongFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with weekday name, month, day and year information.
 * Weekday name and month can be configured to be short or long.
 *
 * @param {DateType} date
 * @param {boolean} [shortWeekday=false] shortWeekday
 * @param {boolean} [shortMonth=false] shortMonth
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with weekday name, month, day and year information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateFull: string = formatDateFull(date);
 * ```
 */
export const formatDateFull = (date: DateType,
                               shortWeekday = false,
                               shortMonth = false,
                               environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(dateFullFormatter(environment,
                                                         shortWeekday,
                                                         shortMonth).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with month, day and year information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month, day and year information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDate: string = formatDateCell(date);
 * ```
 */
export const formatDateCell = (date: DateType,
                               environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(dateCellFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with month, day, year and DOW information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month, day and year information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDate: string = formatDateWithDayOfWeek(date);
 * ```
 */
export const formatDateWithDayOfWeek = (date: DateType,
                                        environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(dateWithDowFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with month, day, year, hour, minute and second information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month, day, year, hour, minute and second information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateTime: string = formatDateTime(date);
 * ```
 */
export const formatDateTime = (date: DateType,
                               environment: UniFocusEnvironment = getEnvironment()): string => {
   const ndate = normalizedDate(date);

   return formatDate(ndate, environment) + ' ' + formatTime(ndate, environment);
};

/**
 * Call this function to convert Date object to localized string with month, day, year long, hour, minute and second information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month, day, year long, hour, minute and second information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateTimeYearLong: string = formatDateTimeYearLong(date);
 * ```
 */
export const formatDateTimeYearLong = (date: DateType,
                                       environment: UniFocusEnvironment = getEnvironment()): string => {
   return formatDateYearLong(date, environment) + ' ' + formatTime(date, environment);
};

/**
 * Call this function to convert Date object to localized string with month, day, year, hour, minute and second information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month, day, year, hour, minute and second information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateTime: string = formatDateTime(date);
 * ```
 */
export const formatTimestamp = (date: DateType,
                                environment: UniFocusEnvironment = getEnvironment()): string => {
   const ndate = normalizedDate(date);

   return formatDate(ndate, environment) + ' ' + removeNonPrintableCharacters(timeFormatterWithSeconds(
      environment).format(ndate));
};

/**
 * Call this function to convert Date object to localized string with hour and minute information.
 *
 * @param {DateType | LocalTime} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with hour and minute information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedTime: string = formatTime(date);
 * ```
 */
export const formatTime = (date: DateType | LocalTime,
                           environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(timeFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with hour, minute, and second
 * information.  The use of this function should be rare, since it includes seconds, which is
 * generally not required.
 *
 * @param {DateType | LocalTime} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with hour, minute, and seconds information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedTime: string = formatTime(date);
 * ```
 */
export const formatTimeWithSeconds = (date: DateType | LocalTime,
                           environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(timeFormatterWithSeconds(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with hour information.
 *
 * @param {DateType | LocalTime} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with hour information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedHour: string = formatHour(date);
 * ```
 */
export const formatHour = (date: DateType | LocalTime,
                           environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(hourFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized narrow weekday name.
 *
 * @param {DateType | DayOfWeek} date or day of week
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @returns {string} Returns the localized narrow weekday name.
 */
export const formatWeekdayNarrow = (date: DateType | DayOfWeek, environment: UniFocusEnvironment = getEnvironment()): string => {
   if (date instanceof DayOfWeek) {
      return removeNonPrintableCharacters(weekdayNarrowFormatter(environment).format(normalizedDate(LocalDate.now().with(TemporalAdjusters.dayOfWeekInMonth(1, date)))));
   }
   return removeNonPrintableCharacters(weekdayNarrowFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized short weekday name.
 *
 * @param {DateType | DayOfWeek} date or day of week
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @returns {string} Returns the localized short weekday name.
 */
export const formatWeekdayShort = (date: DateType | DayOfWeek, environment: UniFocusEnvironment = getEnvironment()): string => {
   if (date instanceof DayOfWeek) {
      return removeNonPrintableCharacters(weekdayShortFormatter(environment).format(normalizedDate(LocalDate.now().with(TemporalAdjusters.dayOfWeekInMonth(1, date)))));
   }
   return removeNonPrintableCharacters(weekdayShortFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized full long weekday name.
 *
 * @param {DateType | DayOfWeek} date or day of week
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @returns {string} Returns the localized long weekday name.
 */
export const formatWeekdayLong = (date: DateType | DayOfWeek, environment: UniFocusEnvironment = getEnvironment()): string => {
   if (date instanceof DayOfWeek) {
      return removeNonPrintableCharacters(weekdayLongFormatter(environment).format(normalizedDate(LocalDate.now().with(TemporalAdjusters.dayOfWeekInMonth(1, date)))));
   }
   return removeNonPrintableCharacters(weekdayLongFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with month and day information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month and day information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateMonthDay: string = formatDateMonthDay(date);
 * ```
 */
export const formatDateMonthDay = (date: DateType,
                                   environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(monthDayFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert Date object to localized string with month and year information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with month and year information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateMonthYear: string = formatDateMonthYear(date);
 * ```
 */
export const formatDateMonthYear = (date: DateType,
                                    environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(monthYearFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert date to localized string with short month name.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with short month name.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateMonthShort: string = formatMonthShort(date);
 * ```
 */
export const formatMonthShort = (date: DateType,
                                 environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(monthShortFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert date to localized string with full month name.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with full month name.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedDateMonthLong: string = formatMonthLong(date);
 * ```
 */
export const formatMonthLong = (date: DateType,
                                environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(monthLongFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to convert date to localized string with full year information.
 *
 * @param {DateType} date
 * @param {UniFocusEnvironment} [environment=getEnvironment()] environment
 * @return Returns localized string with full year information.
 *
 * ```typescript
 * const date: Date = new Date();
 * const formattedYearLong: string = formatYearLong(date);
 * ```
 */
export const formatYearLong = (date: DateType,
                               environment: UniFocusEnvironment = getEnvironment()): string => {
   return removeNonPrintableCharacters(yearLongFormatter(environment).format(normalizedDate(date)));
};

/**
 * Call this function to get localized message based on remaining time left.
 *
 * @param {number} estimatedRemainingTimeInSeconds
 */
export const formatEstimatedRemainingTime = (estimatedRemainingTimeInSeconds: number): string => {

   if (estimatedRemainingTimeInSeconds <= -30) {
      return formatMessageBundle(messages, 'res_processIsTakingLongerThanExpected');
   }
   if (estimatedRemainingTimeInSeconds <= 0) {
      return formatMessageBundle(messages, 'res_finishingUp');
   }

   let minutes: number = Math.floor(estimatedRemainingTimeInSeconds / 60) + (((estimatedRemainingTimeInSeconds % 60) === 0) ? 0 : 1);
   const hours: number = Math.floor(minutes / 60) + (((minutes % 60) > 30) ? 1 : 0);
   minutes = minutes % 60;

   if (minutes === 0) {

      if (hours <= 1) {
         return formatMessageBundle(messages, 'res_aroundOneHourRemaining');
      }
      return formatMessageBundle(messages, 'res_aroundXHoursRemaining', ['' + hours]);
   }

   if (minutes === 1) {

      if (hours === 0) {
         return formatMessageBundle(messages, 'res_aroundOneMinuteRemaining');
      }
      else if (hours === 1) {
         return formatMessageBundle(messages, 'res_aroundOneHourAndOneMinuteRemaining');
      }
      return formatMessageBundle(messages, 'res_aroundXHoursAndOneMinuteRemaining', ['' + hours]);
   }

   const array = [2, 3, 5, 10, 15, 20, 30];
   const len = array.length;
   for (let i = 0; i < len; i++) {
      const value = array[i];

      if (minutes <= value) {

         if (hours === 0) {
            return formatMessageBundle(messages, 'res_aroundXMinutesRemaining', ['' + value]);
         }
         if (hours === 1) {
            return formatMessageBundle(messages,
                                       'res_aroundOneHourAndXMinutesRemaining',
                                       ['' + value]);
         }
         return formatMessageBundle(messages,
                                    'res_aroundXHoursAndXMinutesRemaining',
                                    ['' + hours, '' + value]);
      }
   }

   if (hours <= 1) {
      return formatMessageBundle(messages, 'res_aroundOneHourRemaining');
   }
   return formatMessageBundle(messages, 'res_aroundXHoursRemaining', ['' + hours]);
};

/**
 * Call this function to convert Date object to localized string with difference between now and passed time.
 *
 * @param {DateType} date
 * @return Returns localized string with difference between now and passed time.
 *
 * ```typescript
 * const date: Date = new Date();
 * const timeAgoMessage: string = formatDateFromNow(date);
 * ```
 */
export const formatDateFromNow = (date: DateType): string => {
   return moment(normalizedDate(date)).from(currentDate());
};

export interface RoundedMinute {
   minute: number;
   overHour: number;
}

/**
 * Given a minute and a step value, round the minute up or down to the closest step.  If the
 * minute rounded up to the next hour, return minute 0 and overHour of 1.  Otherwise, return
 * the rounded minute and overHour of 0.
 *
 * @param minute The current minute value
 * @param step The step amount
 */
export const roundMinuteToClosestStepOrUpperHour = (minute: number, step: number): RoundedMinute => {
   const remainder: number = minute % step;

   if (remainder === 0) {
      return {
         minute: minute,
         overHour: 0
      };
   }

   const nextStepValue: number = Math.ceil(minute / step) * step;
   const lowerStepValue: number = Math.ceil(step / 2);

   if (nextStepValue >= 60 && remainder >= lowerStepValue) {
      return {
         minute: 0,
         overHour: 1
      };
   }

   if (remainder < lowerStepValue) {
      return {
         minute: minute - remainder,
         overHour: 0
      };
   }

   return {
      minute: nextStepValue,
      overHour: 0
   };
};
